import BaseService from "./BaseService";
import { newRootStore } from "stores/RootStore";

/**
 * Pipeline step a piece of step-level feedback is attached to. Must match the
 * names of the backend `KboFlowFeedbackStep` enum — ASP.NET Core binds enum
 * route values by name.
 */
export type KboFlowFeedbackStep =
    | "Context"
    | "QuestionGeneration"
    | "QuestionValidation"
    | "Analysis"
    | "Report";

export type KboFlowFeedbackSentiment = "Positive" | "Negative";

export interface KboFlowFeedback {
    id: string;
    sentiment: KboFlowFeedbackSentiment;
    text?: string;
    creatorType: string;
    authorName?: string;
    createdOn: string;
    /** True when the current user authored the entry, so they may delete it. */
    isMine: boolean;
    /** Only populated for step-level feedback. */
    step?: KboFlowFeedbackStep;
}

export interface SubmitKboFlowFeedbackRequest {
    sentiment: KboFlowFeedbackSentiment;
    text?: string;
}

/**
 * Feedback on KBo Flow questions and their pipeline steps, as shown on the run
 * detail page. Question feedback accumulates as a history; step feedback keeps a
 * single row per user per question+step and is updated in place on resubmit.
 * Ultra-only on the backend (see KboFlowFeedbackController).
 */
export class KboFlowFeedbackService extends BaseService {
    protected urlBase = "api/KboFlowFeedback";

    constructor() {
        super();
        // BaseService resolves authorization headers through the global RootStore.
        // This panel can be the first feature loaded in a route, so ensure the
        // singleton exists before its first feedback request.
        newRootStore();
    }

    public async getQuestionFeedback(
        questionId: string,
        abortSignal?: AbortSignal,
    ): Promise<KboFlowFeedback[]> {
        return this.get<KboFlowFeedback[]>(
            new URLSearchParams(),
            `questions/${questionId}`,
            abortSignal,
        );
    }

    public async submitQuestionFeedback(
        questionId: string,
        request: SubmitKboFlowFeedbackRequest,
        abortSignal?: AbortSignal,
    ): Promise<KboFlowFeedback> {
        return this.post<KboFlowFeedback>(
            JSON.stringify(request),
            new URLSearchParams(),
            `questions/${questionId}`,
            { returnJson: true },
            undefined,
            abortSignal,
        );
    }

    public async deleteQuestionFeedback(
        questionId: string,
        feedbackId: string,
    ): Promise<Response> {
        return this.delete(
            new URLSearchParams(),
            `questions/${questionId}/${feedbackId}`,
        );
    }

    public async getStepFeedback(
        questionId: string,
        step: KboFlowFeedbackStep,
        abortSignal?: AbortSignal,
    ): Promise<KboFlowFeedback[]> {
        return this.get<KboFlowFeedback[]>(
            new URLSearchParams(),
            `questions/${questionId}/steps/${step}`,
            abortSignal,
        );
    }

    public async submitStepFeedback(
        questionId: string,
        step: KboFlowFeedbackStep,
        request: SubmitKboFlowFeedbackRequest,
        abortSignal?: AbortSignal,
    ): Promise<KboFlowFeedback> {
        return this.post<KboFlowFeedback>(
            JSON.stringify(request),
            new URLSearchParams(),
            `questions/${questionId}/steps/${step}`,
            { returnJson: true },
            undefined,
            abortSignal,
        );
    }

    public async deleteStepFeedback(
        questionId: string,
        step: KboFlowFeedbackStep,
        feedbackId: string,
    ): Promise<Response> {
        return this.delete(
            new URLSearchParams(),
            `questions/${questionId}/steps/${step}/${feedbackId}`,
        );
    }
}

export default KboFlowFeedbackService;
